import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { urlDoPortal } from '@/lib/urlDoPortal';
import Logo from '@/components/base/Logo';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Informe seu e-mail para continuar.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setError('Insira um e-mail válido.');
      return;
    }

    setLoading(true);

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(trimmed, {
      redirectTo: urlDoPortal('/resetar-senha'),
    });

    if (resetError) {
      setError(
        resetError.message === 'User not found'
          ? 'Nenhuma conta encontrada com este e-mail.'
          : 'Erro ao enviar o e-mail. Tente novamente.',
      );
      setLoading(false);
      return;
    }

    setSent(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center mb-10">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo className="w-10 h-10 object-contain" />
            <span className="font-semibold text-2xl tracking-tight text-foreground-900">
              Central Check
            </span>
          </Link>
        </div>

        <h1 className="text-xl font-bold text-foreground-900 mb-2 text-center">Recuperar senha</h1>
        <p className="text-sm text-foreground-500 mb-8 text-center">
          {sent
            ? 'Enviamos um link de recuperação para o seu e-mail.'
            : 'Digite seu e-mail e enviaremos um link para redefinir sua senha.'}
        </p>

        {sent ? (
          <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-mail-send-line text-2xl text-green-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900 mb-2">Link enviado!</p>
            <p className="text-sm text-foreground-500 mb-6">
              Verifique sua caixa de entrada e siga as instruções do e-mail. O link expira em 1
              hora.
            </p>
            <p className="text-xs text-foreground-400 mb-6">
              Não encontrou? Olhe a pasta de spam ou lixo eletrônico.
            </p>
            <button
              onClick={() => {
                setSent(false);
                setEmail('');
                setError('');
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              Enviar para outro e-mail
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-background-50 rounded-2xl border border-background-200/70 p-6"
          >
            <div className="flex flex-col gap-5">
              <div>
                <label
                  htmlFor="forgot-email"
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  E-mail
                </label>
                <input
                  id="forgot-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@empresa.com"
                  className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                  <i className="ri-error-warning-line"></i>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? 'Enviando...' : 'Enviar link de recuperação'}
              </button>
            </div>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-foreground-600">
          <Link
            to="/login"
            className="text-primary-600 hover:text-primary-700 font-medium transition-colors inline-flex items-center gap-1"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            Voltar para o login
          </Link>
        </p>
      </div>
    </div>
  );
}
